/**
 * Router construction, separated from `main.tsx` so tests can build their own
 * instance (with a memory history) against the exact same route tree the
 * browser gets.
 *
 * `routeTree.gen.ts` is written by `@tanstack/router-plugin` in dev and by
 * `tsr generate` in `build`/`typecheck`. It is the one routing file nobody
 * edits by hand.
 */
import type { QueryClient } from "@tanstack/react-query";
import { createRouter, type RouterHistory } from "@tanstack/react-router";

import { routeTree } from "./routeTree.gen";
import { NotFoundScreen } from "./shell/NotFoundScreen";
import { RouteErrorScreen } from "./shell/RouteErrorScreen";
import { RoutePendingScreen } from "./shell/RoutePendingScreen";

export function createAppRouter({
  queryClient,
  history,
}: {
  queryClient: QueryClient;
  history?: RouterHistory | undefined;
}) {
  return createRouter({
    routeTree,
    context: { queryClient },
    ...(history ? { history } : {}),
    defaultErrorComponent: RouteErrorScreen,
    defaultNotFoundComponent: NotFoundScreen,
    defaultPendingComponent: RoutePendingScreen,
    // Show the pending UI only if a load is actually slow. Flashing a skeleton
    // for 80ms on a cached navigation reads as jank, not as progress.
    defaultPendingMs: 300,
    defaultPendingMinMs: 400,
    // The URL is authoritative, so a scroll position restored from a previous
    // visit to the same href is part of that state.
    scrollRestoration: true,
  });
}

export type typeAppRouter = ReturnType<typeof createAppRouter>;

/**
 * Registers the app's route tree with the library's global types, which is what
 * makes `<Link to="/sign-in">` and `useSearch()` type-check against the real
 * routes — a wrong path fails `tsc --noEmit`, never only in the browser.
 */
declare module "@tanstack/react-router" {
  // MUST stay an `interface`. This is declaration merging into the library's own
  // `Register` interface — a `type` alias cannot merge and makes the whole file
  // fail with "Duplicate identifier 'Register'". `consistent-type-definitions`
  // prefers `type` everywhere else in this repo and is simply not applicable to
  // a module augmentation.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Register {
    router: typeAppRouter;
  }
}
